import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Field as FormikField, Form as FormikForm, useFormikContext } from 'formik'
import styles from './StaffingFirstStructure.module.scss'
import { Button, Field, Title } from '@/ui'
import { Add, Trash } from '@app/icons'
import { useSelector } from 'react-redux'
import { employerProfileSelectors } from '@features/employerProfile/model'
import * as handbookSelectors from '@/entity/handbook/selectors'
import { filterKato } from '@/entity/handbook/thunks'

/**
 * Рекурсивно собирает все используемые deptTypeCode из дерева структур
 * @param {Object} structure - структура организации
 * @param {Set} usedCodes - набор уже использованных кодов
 * @returns {Set} - набор всех используемых deptTypeCode
 */
const collectUsedDeptTypeCodes = (structure, usedCodes = new Set()) => {
	if (!structure) return usedCodes

	// Добавляем текущий код, если он существует
	if (structure.deptTypeCode) {
		usedCodes.add(structure.deptTypeCode)
	}

	// Рекурсивно обходим дочерние элементы
	if (Array.isArray(structure.childs)) {
		structure.childs.forEach((child) => {
			collectUsedDeptTypeCodes(child, usedCodes)
		})
	}

	return usedCodes
}

export const Form = ({ onClose, staffType, actions, staffProject, rowSelectMenu }) => {
	const activeCompany = useSelector(employerProfileSelectors.activeCompany)
	const { t } = useTranslation()
	const { values, setFieldValue } = useFormikContext()
	const { lmc, msu, sphereType } = activeCompany
	const districts = useSelector(handbookSelectors.districts)
	const { staffDeptTypeMsu, staffDeptTypeSchool, staffDeptTypeCulture, staffDeptTypeSport, staffDeptTypeHealth } = useSelector(state => ({
		staffDeptTypeMsu: state.handbook.staffDeptTypeMsu,
		staffDeptTypeSchool: state.handbook.staffDeptTypeSchool,
		staffDeptTypeCulture: state.handbook.staffDeptTypeCulture,
		staffDeptTypeSport: state.handbook.staffDeptTypeSport,
		staffDeptTypeHealth: state.handbook.staffDeptTypeHealth,
	}))
	const [unitRegions, setUnitRegions] = useState(values.units.map(() => []))
	// Поля названий отключены для всех сфер, кроме healthcare
	const isNameFieldsDisabled = sphereType && sphereType !== 'healthcare'

	/**
	 * Получение справочника по типу сферы
	 */
	const getSphereHandbook = useCallback((type) => {
		switch (type) {
			case 'school':
				return staffDeptTypeSchool
			case 'healthcare':
				return staffDeptTypeHealth
			case 'culture':
				return staffDeptTypeCulture
			case 'sport':
				return staffDeptTypeSport
			default:
				return null
		}
	}, [staffDeptTypeSchool, staffDeptTypeHealth, staffDeptTypeCulture, staffDeptTypeSport])

	/**
	 * Сортировка справочников сфер: однобуквенные коды в начале по алфавиту, остальные в конце
	 */
	const sortSphereHandbook = useCallback((items) => {
		if (!items || !Array.isArray(items)) return []

		const singleLetterCodes = []
		const otherCodes = []

		items.forEach(item => {
			// Проверяем, является ли code однобуквенным (одна буква латиницы)
			if (item.code && /^[A-Za-z]$/.test(item.code)) {
				singleLetterCodes.push(item)
			} else {
				otherCodes.push(item)
			}
		})

		// Сортируем однобуквенные коды по алфавиту
		singleLetterCodes.sort((a, b) => a.code.localeCompare(b.code))

		// Объединяем: сначала отсортированные однобуквенные, затем остальные в исходном порядке
		return [...singleLetterCodes, ...otherCodes]
	}, [])

	/**
	 * Отсортированный справочник для текущей сферы
	 */
	const sortedSphereHandbook = useMemo(() => {
		const handbook = getSphereHandbook(sphereType)
		return sortSphereHandbook(handbook)
	}, [sphereType, getSphereHandbook, sortSphereHandbook])

	/**
	 * Фильтрация staffType для обычных организаций
	 */
	const filterStaffType = useCallback((staffType, lmc) => {
		return staffType.filter((item) => {
			// Глобально всегда фильтруем только 'item.code === "1"'
			if (item.code === '1') return false
			// Если lmc не задан, убираем элементы с code '23', иначе оставляем как есть
			return lmc ? true : item.code !== '23'
		})
	}, [])

	/**
	 * Обработчик изменения типа структуры для сфер с автозаполнением названий
	 */
	const handleDeptTypeChangeForSphere = useCallback((code, unitIndex) => {
		setFieldValue(`units.${unitIndex}.deptTypeCode`, code)

		if (sphereType && sortedSphereHandbook) {
			const selectedItem = sortedSphereHandbook.find(item => item.code === code)
			if (selectedItem) {
				setFieldValue(`units.${unitIndex}.dept.nameRu`, selectedItem.rus || '')
				setFieldValue(`units.${unitIndex}.dept.nameKz`, selectedItem.kz || '')
			}
		}
	}, [sphereType, sortedSphereHandbook, setFieldValue])

	/**
	 * Фильтрация справочника для организаций со сферой деятельности
	 * Исключает уже использованные типы структур из выбора
	 * Учитывает как существующие структуры в проекте, так и выбранные в текущей форме
	 */
	const getFilteredOptionsForUnit = useCallback((unitIndex) => {
		if (!sphereType) {
			// Для организаций без sphereType возвращаем соответствующие справочники
			return msu ? staffDeptTypeMsu : filterStaffType(staffType, lmc)
		}

		if (!sortedSphereHandbook) return []

		// Собираем все используемые deptTypeCode из дерева структур проекта
		const usedCodesInProject = staffProject ? collectUsedDeptTypeCodes(staffProject) : new Set()

		// При редактировании разрешаем текущий редактируемый код
		if (actions.edit && rowSelectMenu?.deptTypeCode) {
			usedCodesInProject.delete(rowSelectMenu.deptTypeCode)
		}

		// Собираем коды, выбранные в других units текущей формы
		const usedCodesInForm = new Set()
		values.units.forEach((unit, index) => {
			// Не учитываем текущий unit и пустые значения
			if (index !== unitIndex && unit.deptTypeCode) {
				usedCodesInForm.add(unit.deptTypeCode)
			}
		})

		// Объединяем оба набора запрещённых кодов
		const allUsedCodes = new Set([...usedCodesInProject, ...usedCodesInForm])

		// Фильтруем справочник, исключая все использованные коды

		if(sphereType !== 'healthcare'){
			return sortedSphereHandbook.filter(item => !allUsedCodes.has(item.code))
		} else {
			return sortedSphereHandbook
		}
	}, [
		sphereType, 
		msu, 
		staffDeptTypeMsu, 
		sortedSphereHandbook,
		staffType, 
		lmc, 
		staffProject, 
		actions.edit, 
		rowSelectMenu?.deptTypeCode,
		values.units,
		filterStaffType
	])

	const loadRegions = useCallback(async (code, unitIndex) => {
		if (code) {
			const response = await filterKato(code)
			setUnitRegions((prev) => {
				const updatedRegions = [...prev]
				updatedRegions[unitIndex] = response
				return updatedRegions
			})
		} else {
			setUnitRegions((prev) => {
				const updatedRegions = [...prev]
				updatedRegions[unitIndex] = []
				return updatedRegions
			})
		}
	}, [])

	useEffect(() => {
		values.units.forEach((unit, index) => {
			if (unit.ddistrictCode) {
				loadRegions(unit.ddistrictCode, index)
			}
		})
	}, [values.units, loadRegions])

	const addStructuralUnit = () => {
		setFieldValue('units', [
			...values.units,
			{
				subsidiary: false,
				binBranch: '',
				deptTypeCode: '',
				dept: {
					nameKz: '',
					nameRu: '',
					code: '',
				},
				ddistrictCode: '',
				dregionCode: '',
			},
		])
		setUnitRegions([...unitRegions, []]) // Add empty array for new unit
	}

	const removeUnits = (index) => {
		const updatedUnits = [...values.units]
		updatedUnits.splice(index, 1)
		setFieldValue('units', updatedUnits)
		setUnitRegions((prev) => {
			const updatedRegions = [...prev]
			updatedRegions.splice(index, 1)
			return updatedRegions
		})
	}

	return (
		<FormikForm className={styles.form}>
			<section>
				<div className={styles.item}>
					<div className={`${styles.fields} ${styles.fields_verticalCenter}`}>
						<Title>{t('staff_modal.staff_information')}</Title>
					</div>
					{values.units.map((unit, unitIndex) => (
						<React.Fragment key={unitIndex}>
							<div className={`${styles.fields} ${styles.threeFields}`}>
								{unit.subsidiary ? (
									<FormikField name={`units.${unitIndex}.binBranch`}>
										{({ field, meta: { touched, error } }) => (
											<Field
												type="number"
												hideNumberArrows
												label={t('staff_modal.bin')}
												placeholder={t('staff_modal.bin_placeholder')}
												isRequired
												error={touched && error}
												{...field}
												maxLength={12}
											/>
										)}
									</FormikField>
								) : (
									<FormikField name={`units.${unitIndex}.deptTypeCode`}>
										{({ field, meta: { touched, error } }) => (
											<Field
												{...field}
												type="text"
												fieldType="selectSearch"
												label={t('staff_modal.structure_type')}
												placeholder={t('staff_modal.structure_type_placeholder')}
												isRequired
												error={touched && error}
												options={getFilteredOptionsForUnit(unitIndex)}
												onChange={sphereType ? (code) => handleDeptTypeChangeForSphere(code, unitIndex) : field.onChange}
											/>
										)}
									</FormikField>
								)}
							</div>
							<FormikField name={`units.${unitIndex}.dept.nameKz`}>
								{({ field, meta: { touched, error } }) => (
									<Field
										disabled={isNameFieldsDisabled}
										type="text"
										isRequired={(unit.dept.nameRu?.length || unit.dept.nameKz?.length) <= 1}
										label={t('staff_modal.structure_name_kz')}
										placeholder={t('staff_modal.structure_name_placeholder_kz')}
										error={touched && error}
										{...field}
									/>
								)}
							</FormikField>
							<FormikField name={`units.${unitIndex}.dept.nameRu`}>
								{({ field, meta: { touched, error } }) => (
									<Field
										disabled={isNameFieldsDisabled}
										type="text"
										isRequired={(unit.dept.nameRu?.length || unit.dept.nameKz?.length) <= 1}
										label={t('staff_modal.structure_name')}
										placeholder={t('staff_modal.structure_name_placeholder')}
										error={touched && error}
										{...field}
									/>
								)}
							</FormikField>
							{/*для ЦТМ с типом Карьерный центр*/}
							{unit.deptTypeCode === '23' && (
								<>
									<FormikField name={`units.${unitIndex}.ddistrictCode`}>
										{({ field, meta: { touched, error } }) => {
											const onChange = (code) => {
												loadRegions(code, unitIndex)
												setFieldValue(`units.${unitIndex}.ddistrictCode`, code)
												setFieldValue(`units.${unitIndex}.dregionCode`, null)
											}
											return (
												<Field
													type="text"
													fieldType="select"
													label={t('contracts_tab.region_work')}
													placeholder={t('choose_from_handbook')}
													options={districts}
													isRequired
													error={touched && error}
													{...field}
													onChange={onChange}
												/>
											)
										}}
									</FormikField>
									<FormikField name={`units.${unitIndex}.dregionCode`}>
										{({ field, meta: { touched, error } }) => {
											const onChange = (code) => {
												setFieldValue(`units.${unitIndex}.dregionCode`, code)
											}
											return (
												<Field
													type="text"
													fieldType="select"
													label={t('contracts_tab.distrcit_work')}
													placeholder={t('choose_from_handbook')}
													options={unitRegions[unitIndex]}
													isRequired
													error={touched && error}
													{...field}
													onChange={onChange}
												/>
											)
										}}
									</FormikField>
								</>
							)}
							{actions.create
								? unitIndex > 0 && (
										<Button
											transparent
											onClick={() => removeUnits(unitIndex)}
											buttonClassName={styles.buttonRed}
											disabled={false}
										>
											<Trash className={styles.iconRed} />
											{t('remove')}
										</Button>
								  )
								: null}
						</React.Fragment>
					))}
					{actions.create ? (
						<Button transparent onClick={addStructuralUnit} buttonClassName={styles.button}>
							<Add className={styles.iconGreen} />
							{t('staff_modal.structure_add')}
						</Button>
					) : null}
				</div>
			</section>
			<div className={styles.actions}>
				<Button type="bare" onClick={onClose}>
					{t('cancel')}
				</Button>
				<Button textColor="green" borderColor="green" transparent border type="submit">
					{t('save')}
				</Button>
			</div>
		</FormikForm>
	)
}
